import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ShoppingCart, Heart, Loader2, User, LogOut } from "lucide-react";
import { useAppContext } from "../../contexts/AppContext";
import { API_URL } from "../../constant";
import { createPortal } from "react-dom";

const CardMagnifier = ({ src, alt }) => {
  const [showMagnifier, setShowMagnifier] = useState(false);

  return (
    <>
      <div
        className="magnifier-container"
        onMouseEnter={() => setShowMagnifier(true)}
        onMouseLeave={() => setShowMagnifier(false)}
        onClick={() => setShowMagnifier(true)}
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <img
          src={src}
          alt={alt}
          className="magnifier-image"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
        />
      </div>

      {/* Center Modal via Portal */}
      {showMagnifier && typeof document !== "undefined" && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            pointerEvents: window.innerWidth <= 768 ? 'auto' : 'none',
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => {
            if (window.innerWidth <= 768) setShowMagnifier(false);
          }}
        >
          <img
            src={src}
            alt={alt + " Full"}
            style={{ maxWidth: '90%', maxHeight: '85vh', objectFit: 'contain' }}
          />
          {window.innerWidth <= 768 && (
            <div style={{ color: 'white', marginTop: '1.5rem', fontWeight: 'bold', fontSize: '1.125rem' }}>
              Tap anywhere to close
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export function CardSelection({ onAddToCart }) {
  const [wishlist, setWishlist] = useState({});
  const [tiers, setTiers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const { user, handleLogout, cartItems, setLoginModalOpen } = useAppContext();
  const cartCount = cartItems ? cartItems.reduce((acc, item) => acc + item.qty, 0) : 0;
  const cartTotal = cartItems ? cartItems.reduce((acc, item) => acc + item.price * item.qty, 0) : 0;

  useEffect(() => {
    const fetchCards = async () => {
      try {
        const apiUrl = API_URL;
        const res = await fetch(`${apiUrl}/api/collectible-cards`);
        const data = await res.json();
        
        if (data.status === "success" && Array.isArray(data.data)) {
          const mappedTiers = data.data.map((card) => {
            const price = parseFloat(card.market_value || "0");
            let coins = price;
            if (card.name.includes("Silver")) coins = 2;
            else if (card.name.includes("Bronze")) coins = 4;
            else if (card.name.includes("Gold")) coins = 8;
            
            return {
              id: String(card.id),
              name: card.name.replace("Alpha ", ""),
              price: price,
              topCoins: coins, 
              bonus: price, 
              image: card.image_url
            };
          });
          setTiers(mappedTiers);
        }
      } catch (err) {
        console.error("Failed to fetch collectible cards", err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCards();
  }, []);

  const toggleWishlist = (id) => {
    setWishlist((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <>
      <style>
        {`
          .custom-tier-card {
            transition: transform 0.5s ease, box-shadow 0.3s ease;
          }
          .custom-tier-card:hover {
            transform: scale(1.05);
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          }
        `}
      </style>
      <section id="captain-cards" className="w-full bg-[#f9fafb] py-12 px-4 md:px-8 border-b border-gray-200" style={{ backgroundColor: '#f9fafb', width: '100%', padding: '3rem 1rem', borderBottom: '1px solid #e5e7eb' }}>
      <div className="max-w-7xl mx-auto space-y-8" style={{ maxWidth: '80rem', margin: '0 auto' }}>
        {/* Section Header */}
        <div className="flex flex-col items-center justify-center gap-4 text-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', textAlign: 'center', marginBottom: '2rem' }}>
          <div className="flex items-center justify-center gap-4 w-full" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', width: '100%' }}>
            <div className="h-[1px] w-12 md:w-24 bg-amber-400" style={{ height: '1px', width: '3rem', backgroundColor: '#fbbf24' }} />
            <h2 className="text-xl md:text-2xl font-extrabold text-[#03173d]" style={{ fontSize: '1.5rem', fontWeight: 800, color: '#03173d', margin: 0 }}>
              Choose Your Captain TC Card
            </h2>
            <div className="h-[1px] w-12 md:w-24 bg-amber-400" style={{ height: '1px', width: '3rem', backgroundColor: '#fbbf24' }} />
          </div>

          <div className="inline-flex items-center gap-6 bg-white shadow-sm border border-gray-100 rounded-lg pl-5 pr-6 py-2.5 z-10 -mt-1 relative border-l-8 border-l-[#03173d]" style={{ display: 'inline-flex', alignItems: 'center', gap: '1.5rem', backgroundColor: '#fff', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', border: '1px solid #f3f4f6', borderRadius: '0.5rem', padding: '0.625rem 1.5rem 0.625rem 1.25rem', zIndex: 10, marginTop: '-0.25rem', position: 'relative', borderLeft: '8px solid #03173d' }}>
            {user ? (
              <div className="flex items-center gap-4" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span className="flex items-center gap-2 text-sm font-semibold text-[#03173d]" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#03173d' }}>
                  <User className="h-4 w-4 text-amber-500" style={{ height: '1rem', width: '1rem', color: '#f59e0b' }} />
                  {user.name}
                </span>
                <button onClick={() => handleLogout()} className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-red-600 transition-colors" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem', fontWeight: 600, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}>
                  <LogOut className="h-4 w-4" style={{ height: '1rem', width: '1rem' }} />
                  Logout
                </button>
              </div>
            ) : (
              <button onClick={() => setLoginModalOpen(true)} className="flex items-center gap-2 text-sm font-semibold text-gray-800 transition-colors hover:text-[#03173d]" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#1f2937', background: 'none', border: 'none', cursor: 'pointer' }}>
                <User className="h-4 w-4 text-amber-500" style={{ height: '1rem', width: '1rem', color: '#f59e0b' }} />
                Login
              </button>
            )}

            <Link to="/purchase/cart" className="flex items-center gap-2 text-sm font-bold text-[#03173d] transition-colors hover:text-blue-900 border-l border-gray-200 pl-6" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: 700, color: '#03173d', textDecoration: 'none', borderLeft: '1px solid #e5e7eb', paddingLeft: '1.5rem' }}>
              <div className="relative" style={{ position: 'relative' }}>
                <ShoppingCart className="h-5 w-5 text-[#03173d]" style={{ height: '1.25rem', width: '1.25rem', color: '#03173d' }} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[10px] font-extrabold text-white shadow" style={{ position: 'absolute', top: '-0.5rem', right: '-0.5rem', display: 'flex', height: '1rem', width: '1rem', alignItems: 'center', justifyContent: 'center', borderRadius: '9999px', backgroundColor: '#f59e0b', fontSize: '10px', fontWeight: 800, color: '#fff', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)' }}>
                    {cartCount}
                  </span>
                )}
              </div>
              <span>£{cartTotal.toFixed(2)}</span>
            </Link>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-20" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '5rem 0' }}>
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" style={{ width: '2rem', height: '2rem', color: '#f59e0b' }} />
          </div>
        ) : (
          /* Tier Grid */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem' }}>
            {tiers.map((tier) => (
              <div
                key={tier.id}
                className="custom-tier-card bg-white transition-transform duration-500 hover:scale-110 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-3 flex flex-col justify-between space-y-5"
                style={{ backgroundColor: '#fff', borderRadius: '0.75rem', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', padding: '1rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '1rem' }}
              >
                <div>
                  <h3 className="text-lg font-bold text-[#03173d] mb-4" style={{ fontSize: '1.125rem', fontWeight: 700, color: '#03173d', marginBottom: '1rem', margin: 0 }}>
                    {tier.name}
                  </h3>

                  <div className="flex gap-2 items-start" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                    {/* Card Thumbnail */}
                    <div className="relative w-24 h-36 flex-shrink-0 rounded-lg border border-amber-300 shadow-sm bg-black" style={{ position: 'relative', width: '6rem', height: '8.5rem', flexShrink: 0, borderRadius: '0.5rem', overflow: 'visible', border: '1px solid #fcd34d', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', backgroundColor: '#000' }}>
                      <CardMagnifier src={tier.image} alt={tier.name} />
                    </div>

                    <ul className="flex-1 space-y-2 text-xs text-gray-700 font-medium" style={{ flex: 1, margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.75rem', color: '#374151', fontWeight: 500 }}>
                      <li className="flex items-start gap-1.5" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.375rem' }}>
                        <CheckCircle2 className="w-4 h-4 text-blue-900 fill-blue-900 stroke-white flex-shrink-0 mt-0.5" style={{ width: '1rem', height: '1rem', color: '#1e3a8a', fill: '#1e3a8a', stroke: '#fff', flexShrink: 0, marginTop: '0.125rem' }} />
                        <span style={{ lineHeight: '1.3' }}>
                          Receive <strong>{tier.topCoins} TopCoins</strong> with every qualifying purchase.
                        </span>
                      </li>
                    </ul>
                  </div>

                  <p className="text-[11px] text-gray-500 font-medium mt-4 italic" style={{ fontSize: '11px', color: '#6b7280', fontWeight: 500, marginTop: '1rem', fontStyle: 'italic', marginBottom: 0 }}>
                    Unique serial number allocated after purchase.
                  </p>

                  <div className="mt-3 text-xl font-black text-[#03173d]" style={{ marginTop: '0.75rem', fontSize: '1.25rem', fontWeight: 900, color: '#03173d' }}>
                    £{tier.price.toFixed(2)}
                  </div>
                </div>

                {/* Bottom Actions */}
                <div className="flex items-center gap-3 pt-2" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', paddingTop: '0.5rem' }}>
                  <button
                    onClick={() => onAddToCart(tier)}
                    className="flex-1 bg-[#03173d] hover:bg-[#072255] text-white font-bold text-xs py-2.5 px-3 rounded-md flex items-center justify-center gap-2 transition-colors shadow-sm"
                    style={{ flex: 1, backgroundColor: '#03173d', color: '#fff', fontWeight: 700, fontSize: '0.75rem', padding: '0.625rem 1rem', borderRadius: '0.375rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', border: 'none', cursor: 'pointer', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}
                  >
                    <ShoppingCart className="w-4 h-4" style={{ width: '1rem', height: '1rem' }} />
                    <span>Add to Cart</span>
                  </button>

                  <button
                    onClick={() => toggleWishlist(tier.id)}
                    aria-label="Add to Wishlist"
                    className={`p-2.5 rounded-md border border-gray-300 transition-colors ${
                      wishlist[tier.id]
                        ? "bg-red-50 text-red-500 border-red-200"
                        : "hover:bg-gray-100 text-gray-600"
                    }`}
                    style={{ padding: '0.625rem', borderRadius: '0.375rem', border: `1px solid ${wishlist[tier.id] ? '#fecaca' : '#d1d5db'}`, backgroundColor: wishlist[tier.id] ? '#fef2f2' : 'transparent', color: wishlist[tier.id] ? '#ef4444' : '#4b5563', cursor: 'pointer' }}
                  >
                    <Heart
                      className={`w-4 h-4 ${
                        wishlist[tier.id] ? "fill-red-500 stroke-red-500" : ""
                      }`}
                      style={{ width: '1rem', height: '1rem', fill: wishlist[tier.id] ? '#ef4444' : 'none', stroke: wishlist[tier.id] ? '#ef4444' : 'currentColor' }}
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
    </>
  );
}
